'use client';

import { useMemo, useCallback } from 'react';
import type { CartItem, Product } from '@/lib/pricing';
import { calcSubtotal, calcReferralDiscount } from '@/lib/pricing';
import { useCartContext } from '@/components/CartProvider';

export interface BundleConstituent {
  productId: string;
  region: string;
  shellsPerUnit: number;
  qty: number;
  canFulfill: boolean;
}

export interface StockInfo {
  isAvailable: boolean;
  shellsNeeded: number;
  /** Shell cost in SG region for this product */
  shellsSg: number;
  /** Shell cost in MY region for this product */
  shellsMy: number;
  /** Shell cost in ID region for this product */
  shellsId: number;
  maxShellsInSingleAccount: number;
  totalAvailableShells: number;
  defaultRegion: string;
  maxQty: number;
  isBundle?: boolean;
  /** Bundle constituents with per-region shell costs (only for bundles). */
  constituents?: BundleConstituent[];
}

/** Per-region account pool data from the stock-availability API summary. */
export interface RegionPool {
  accounts: number;
  totalShells: number;
  maxAccountShells: number;
  /** Sorted descending list of per-account available shells. */
  accountShells: number[];
  /** Shells reserved for pending public orders (RegionInventory.reserved). */
  publicReserved: number;
}

export interface UseCartReturn {
  cart: Record<string, CartItem>;
  setCart: React.Dispatch<React.SetStateAction<Record<string, CartItem>>>;
  clearCart: () => void;
  addToCart: (key: string, item: CartItem) => void;
  removeFromCart: (key: string) => void;
  updateQty: (key: string, qty: number) => void;
  /** Dynamic maxQty per product, accounting for region shell pool usage. */
  effectiveMaxQty: Record<string, number>;
  /** Raw subtotal (LKR, non-channel items only). */
  subtotal: number;
  /** Referral discount applied. */
  referralDiscount: number;
  /**
   * Final payable amount: subtotal - referralDiscount + optional processing charge.
   * When processingChargeEnabled is true, this includes the charge.
   * Use `totalBeforeCharge` if you need the pre-charge amount.
   */
  total: number;
  /** subtotal - referralDiscount (before any processing charge is added). */
  totalBeforeCharge: number;
  /** true if any standard cart item is out of stock */
  hasOutOfStockItems: boolean;
  /** list of out-of-stock product names in the cart */
  outOfStockNames: string[];
  cartItemCount: number;
  cartQtyTotal: number;
}

export interface ChargesInfo {
  processingChargeEnabled: boolean;
  processingChargePercent: number;
}

/**
 * Encapsulates all cart state, the complicated effectiveMaxQty calculation,
 * and subtotal/discount math that was previously inlined in LandingClient.
 */
export function useCart(
  products: Product[],
  stockAvailability: Record<string, StockInfo>,
  referralBalance: number,
  useReferralBalance: boolean,
  charges?: ChargesInfo,
  activeOffers?: Record<string, any>,
): UseCartReturn {
  const {
    cart,
    setCart,
    clearCart,
    addToCart,
    removeFromCart,
    updateQty,
  } = useCartContext();

  // ── effectiveMaxQty ────────────────────────────────────────────────
  // Adjust per-product maxQty based on aggregate shell pool capacity
  // across regions, subtracting shells consumed by OTHER cart items in
  // the same region.
  const effectiveMaxQty = useMemo(() => {
    const result: Record<string, number> = {};

    // Pass 1 — total shells consumed per region
    const cartShellsByRegion: Record<string, number> = {};
    for (const [itemId, item] of Object.entries(cart)) {
      if (item.isChannelItem) continue;
      const info = stockAvailability[itemId];
      if (!info) continue;
      const qty = item.qty || 0;
      if (qty <= 0) continue;

      // For bundles, distribute shells across constituent regions
      if (info.isBundle && info.constituents && info.constituents.length > 0) {
        for (const c of info.constituents) {
          const cRegion = (c.region || 'MY').toUpperCase();
          const shellsPerBundleUnit = c.shellsPerUnit * c.qty;
          if (shellsPerBundleUnit > 0) {
            cartShellsByRegion[cRegion] =
              (cartShellsByRegion[cRegion] || 0) + shellsPerBundleUnit * qty;
          }
        }
      } else if (info.shellsNeeded > 0) {
        const region = info.defaultRegion || 'MY';
        cartShellsByRegion[region] =
          (cartShellsByRegion[region] || 0) + info.shellsNeeded * qty;
      }
    }

    // Pass 2 — per-product effective max
    for (const [prodId, info] of Object.entries(stockAvailability)) {
      if (!info.isAvailable) {
        result[prodId] = 0;
        continue;
      }
      const apiMaxQty = info.maxQty;

      // Bundles have cross-region constituents handled by useStockEngine;
      // use apiMaxQty directly here to avoid incorrect single-region math.
      if (info.isBundle) {
        result[prodId] = apiMaxQty;
        continue;
      }

      const region = info.defaultRegion || 'MY';

      const thisProductCartQty = cart[prodId]?.qty || 0;
      const thisProductCartShells = info.shellsNeeded * thisProductCartQty;
      const totalRegionCartShells = cartShellsByRegion[region] || 0;
      const otherRegionCartShells =
        totalRegionCartShells - thisProductCartShells;

      const regionTotalShells = info.totalAvailableShells || 0;
      const remainingShells = Math.max(
        0,
        regionTotalShells - otherRegionCartShells,
      );
      const capacityMaxQty =
        info.shellsNeeded > 0
          ? Math.floor(remainingShells / info.shellsNeeded)
          : 99;

      result[prodId] = Math.min(apiMaxQty, capacityMaxQty);
    }
    return result;
  }, [stockAvailability, cart]);

  // ── Pricing ────────────────────────────────────────────────────────
  const subtotal = useMemo(
    () => calcSubtotal(cart, products, activeOffers),
    [cart, products, activeOffers],
  );

  const referralDiscount = useMemo(
    () => calcReferralDiscount(subtotal, referralBalance, useReferralBalance),
    [subtotal, referralBalance, useReferralBalance],
  );

  const cartQtyTotal = useMemo(
    () => Object.values(cart).reduce((acc, item) => acc + item.qty, 0),
    [cart],
  );

  const total = useMemo(() => {
    let t = subtotal - referralDiscount;
    if (
      charges?.processingChargeEnabled &&
      charges.processingChargePercent > 0 &&
      cartQtyTotal > 0
    ) {
      const charge = Math.ceil((t * charges.processingChargePercent) / 100);
      t += charge;
    }
    return t;
  }, [subtotal, referralDiscount, charges, cartQtyTotal]);

  const totalBeforeCharge = useMemo(
    () => subtotal - referralDiscount,
    [subtotal, referralDiscount],
  );

  // ── Stock helpers ──────────────────────────────────────────────────
  const outOfStockEntries = useMemo(
    () =>
      Object.entries(cart).filter(([itemId]) => {
        const info = stockAvailability[itemId];
        return info && !info.isAvailable;
      }),
    [cart, stockAvailability],
  );

  const hasOutOfStockItems = outOfStockEntries.length > 0;

  const outOfStockNames = useMemo(
    () =>
      outOfStockEntries.map(([itemId]) => {
        const product = products.find((p) => p.id === itemId);
        return product?.name || itemId;
      }),
    [outOfStockEntries, products],
  );

  // Mutations are supplied directly by the CartProvider context

  const cartItemCount = Object.keys(cart).length;

  return {
    cart,
    setCart,
    clearCart,
    addToCart,
    removeFromCart,
    updateQty,
    effectiveMaxQty,
    subtotal,
    referralDiscount,
    total,
    totalBeforeCharge,
    hasOutOfStockItems,
    outOfStockNames,
    cartItemCount,
    cartQtyTotal,
  };
}
